import { Suspense } from "react";
import {
  Await,
  PrefetchPageLinks,
  useLoaderData,
  useLocation,
} from "react-router";

import { XMarkIcon } from "@heroicons/react/20/solid";

import Breadcrumbs, { BreadcrumbsSkeleton } from "~/components/breadcrumbs";
import { HorizontalSlider } from "~/components/horizontal-slider";
import { SearchProvider } from "~/components/meganav/search-context";
import {
  FacetedRefinementsSlot,
  FacetedRefinementsSlotContent,
} from "~/components/product-list/refinements";
import { Sort } from "~/components/product-list/sort";
import { SearchInput } from "~/components/search-input";
import { SkeletonAwait } from "~/components/suspense/skeleton";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { FilterIcon } from "~/components/ui/icons/filter-icon";
import { P } from "~/components/ui/text";
import { HeroSection } from "~/contentful/components/hero-section";
import { useURL } from "~/contexts";
import type { ProductHitWithSwatches } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { toProductUrl, toVariantURL } from "~/lib/utils/url";
import { ProductTile } from "~/routes/product-list/components/product-tile";

import { headers as headersDefinition } from "./components/headers";
import { LoadMore } from "./components/load-more";
import { Skeletons, SkeletonsFilters } from "./components/skeletons";
import { loader as productListLoader } from "./loader/product-list-loader";
import type { ProductListHystoryState } from "./location-state/product-list.location-state";
import {
  useClearFilters,
  useFilterButtonClick,
  useProductListData,
  useProductListFilters,
  useSortButton,
} from "./utils/product-list-hooks";

export const loader = productListLoader;
export const headers = headersDefinition;

export const handle: AppHandle = {
  meganav: ({ heroSlotData }: SerializeFrom<typeof loader>) => {
    const themeData = heroSlotData.items[0];
    return {
      variant: themeData ? themeData.fields.navigationTextColor : "dark",
    };
  },
  contentRootHeroSlot({ heroSlotData, preview }: SerializeFrom<typeof loader>) {
    const data = heroSlotData.items[0];
    if (!data) return <></>;
    const contentType = data.fields.heroContent?.sys.contentType.sys.id;
    return (
      <div
        className={cn(
          "relative flex overflow-hidden",
          contentType !== "textBlock" && "mt-nav-adjust",
        )}
      >
        <HeroSection originalContent={heroSlotData} preview={preview} />
      </div>
    );
  },
};

function FirstProductPrefetch({
  first,
}: {
  first?: ProductHitWithSwatches | undefined;
}) {
  const url = useURL();
  if (!first) return null;
  const vg = first.c_swatches?.[0]?.variationGroup;
  const page = vg
    ? url(toVariantURL(vg))
    : first.c_productInfo
      ? url(toProductUrl(first.c_productInfo))
      : "";
  return page ? <PrefetchPageLinks page={page} /> : null;
}

export default function CategoryPage() {
  const {
    data,
    itemsPerPage,
    offset,
    categories,
    recomendationData,
    searchNoResultMessages,
  } = useLoaderData<typeof loader>();

  const {
    filtersOpen,
    setFiltersOpen,
    sortingOpen,
    setSortingOpen,
    openFilterId,
    setOpenFilterId,
    onFilterOpen,
  } = useProductListFilters();

  const {
    state,
    searchParams,
    setSearchParams,
    totalProductsRef,
    allHitsRef,
    selectedRefinements,
    numberOfFiltersApplied,
    query,
  } = useProductListData();

  const clearFiltersButtons = useClearFilters(setSearchParams, false);
  const sortButton = useSortButton(setSearchParams, setSortingOpen, false);
  const filterButtonClick = useFilterButtonClick(setSearchParams, false);
  const location = useLocation();
  const url = useURL();

  return (
    <>
      <section className="py-2" key={location.key}>
        <div className="container mx-auto flex flex-wrap items-center pb-12 pt-4">
          <h2 className="hidden">Products list page</h2>
          <nav id="store" className="top-0 w-full px-6 py-1">
            <SkeletonAwait
              resolve={categories}
              skeleton={<BreadcrumbsSkeleton />}
            >
              {categories => <Breadcrumbs categories={categories} />}
            </SkeletonAwait>
            <div className="flex justify-center pt-2">
              {query && (
                <SearchProvider>
                  <SearchInput />
                </SearchProvider>
              )}
            </div>
            <Suspense fallback={<SkeletonsFilters />}>
              <Await resolve={data}>
                {data => (
                  <>
                    <FirstProductPrefetch
                      first={
                        (data.hits?.[0] ?? undefined) as
                          | ProductHitWithSwatches
                          | undefined
                      }
                    />
                    <div className="container mx-auto mt-0 flex flex-col items-center justify-between">
                      {data.total ? (
                        <>
                          <P className="w-full pb-4 text-left text-sm text-text-dark-on-light">
                            {data.query && data.total && (
                              <>
                                <span className="font-normal">
                                  {data.total !== 1
                                    ? "Search Results"
                                    : "Result"}
                                </span>
                                <span className="font-bold">
                                  {" "}
                                  for {query || ""}
                                </span>
                              </>
                            )}
                          </P>
                          <P className="text-dark-on-light w-full pb-6 text-left text-sm font-normal sm-max:pb-0">
                            {"Showing"} {data.total} {"items"}
                          </P>

                          <div className="flex w-full gap-3 text-xs sm-max:hidden">
                            <div className="w-[60%] flex-1">
                              <HorizontalSlider
                                controlsEnabled
                                className="relative"
                              >
                                <span className="py-3 text-base font-semibold text-neutral-11">
                                  Filter by:
                                </span>
                                <FacetedRefinementsSlot
                                  refinements={data.refinements}
                                  onFilterOpen={onFilterOpen}
                                />
                              </HorizontalSlider>
                            </div>
                            {clearFiltersButtons()}
                            {sortButton(searchParams)}
                          </div>

                          <div className="mx-auto flex w-full flex-wrap items-center pt-6 text-xs 2xl:hidden md-only:hidden">
                            <div className="w-1/2 px-2">
                              <Button
                                type="button"
                                className="w-full"
                                variant="outline"
                                onClick={() => {
                                  setFiltersOpen(true);
                                  setOpenFilterId("");
                                }}
                              >
                                <FilterIcon />
                                <span className="mx-2">Filter</span>
                                {numberOfFiltersApplied > 0 && (
                                  <Badge
                                    variant="secondary"
                                    className="rounded-sm px-1.5 font-light"
                                  >
                                    {numberOfFiltersApplied}
                                    <Button
                                      variant="linkInline"
                                      className="h-[18px] w-[18px] bg-transparent text-neutral-9"
                                      onClick={filterButtonClick}
                                    >
                                      <XMarkIcon />
                                    </Button>
                                  </Badge>
                                )}
                              </Button>
                            </div>

                            <div className="w-1/2 px-2">
                              {sortButton(searchParams, "w-full")}
                            </div>
                            {clearFiltersButtons("w-full no-underline")}
                          </div>
                        </>
                      ) : (
                        <>
                          <Suspense fallback={<div>Loading...</div>}>
                            <Await resolve={searchNoResultMessages}>
                              {messages => null}
                            </Await>
                          </Suspense>
                        </>
                      )}
                    </div>
                  </>
                )}
              </Await>
            </Suspense>
          </nav>

          {/* Product list */}

          {/* render predata immediately without suspense */}
          {state && "predata" in state && state.predata?.hits && (
            <>
              {state.predata.hits.map(
                (hit: ProductHitWithSwatches, i: number) => (
                  <ProductTile
                    key={`predata-${hit.productId}-${i}`}
                    product={hit}
                    quickNavRef={allHitsRef}
                    totalProductsRef={totalProductsRef}
                    className="w-1/2 p-0 px-2 py-6 sm:px-6 md:w-1/3 xl:w-1/4"
                  />
                ),
              )}
            </>
          )}

          {/* render new data with suspense */}
          <Suspense fallback={<Skeletons />}>
            <Await resolve={data}>
              {data => {
                const newHits = data.hits ?? [];
                const allHits = (state?.predata?.hits ?? []).concat(newHits);
                const totalShowing = allHits.length;
                const hasMorePages = offset + itemsPerPage < data.total;

                // Update refs so predata tiles can access current data
                totalProductsRef.current = data.total;
                allHitsRef.current = allHits;

                return (
                  <>
                    {newHits.map(hit => (
                      <ProductTile
                        key={`new-${hit.productId}`}
                        product={hit}
                        quickNavRef={allHitsRef}
                        totalProductsRef={totalProductsRef}
                        className="w-1/2 p-0 px-2 py-6 sm:px-6 md:w-1/3 xl:w-1/4"
                      />
                    ))}
                    {hasMorePages && (
                      <LoadMore
                        data={
                          {
                            predata: {
                              ...data,
                              hits: allHits,
                            },
                          } satisfies ProductListHystoryState
                        }
                      >
                        Showing {totalShowing} of {data.total} products
                      </LoadMore>
                    )}
                  </>
                );
              }}
            </Await>
          </Suspense>
        </div>
      </section>
      <Dialog open={filtersOpen} onOpenChange={() => setFiltersOpen(false)}>
        <DialogContent
          forceMount
          className="left-0 block h-screen w-full translate-x-0 overflow-y-auto bg-page-background sm:max-w-sm"
        >
          <div className="text-semibold w-fit justify-start py-[13px] text-2xl">
            Filter by
          </div>
          <div className="mx-auto flex flex-col flex-wrap items-center gap-3 bg-white">
            <Suspense fallback={<SkeletonsFilters />}>
              <Await resolve={data}>
                {data => {
                  return (
                    <>
                      <FacetedRefinementsSlotContent
                        refinements={data.refinements}
                        openFilterId={openFilterId}
                        onToggle={() => setOpenFilterId("")}
                      />
                    </>
                  );
                }}
              </Await>
            </Suspense>
          </div>
          <div className="sticky bottom-[-20px] left-0 right-0 flex items-center justify-center bg-panel-translucent py-[20px]">
            <Button
              variant="default"
              className="text-base font-semibold text-white"
              onClick={() => setFiltersOpen(false)}
            >
              View Results
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={sortingOpen} onOpenChange={() => setSortingOpen(false)}>
        <DialogContent className="left-0 block h-screen w-full translate-x-0 overflow-y-auto sm:max-w-sm">
          <div className="text-semibold w-fit justify-start py-[13px] text-2xl">
            Sort by
          </div>
          <div className="mx-auto flex flex-col flex-wrap items-center gap-3 bg-white px-[24px]">
            <Suspense fallback={<SkeletonsFilters />}>
              <Await resolve={data}>
                {data => {
                  return (
                    <>
                      <Sort options={data.sortingOptions} />
                    </>
                  );
                }}
              </Await>
            </Suspense>
          </div>
          <div className="sticky bottom-[-20px] left-0 right-0 flex items-center justify-center bg-panel-translucent py-[20px]">
            <Button
              variant="default"
              className="text-base font-semibold text-white"
              onClick={() => setSortingOpen(false)}
            >
              View Results
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
